import { config, getApiOrigin, getCheckoutOrigin } from '../config';
import type {
  ElementsSubmitError,
  PaypercutBillingDetails,
  PaypercutConfirmPaymentResult,
} from '../types/elements';
import { generateId } from '../utils/generate-id';
import { ElementsActionShell } from './action-shell';
import {
  ELEMENTS_PAYMENT_CONFIRMATION_PROTOCOL_VERSION,
  isElementsPaymentConfirmationMessage,
  type ElementsPaymentConfirmationStart,
  type ElementsPaymentConfirmationUseSDKAction,
} from './payment-confirmation-protocol';
import { createSubmitError } from './payment-method-submission';
import { hasExactKeys, isBoundedProtocolString, isRecord } from './protocol-validation';

const CONFIRMATION_TIMEOUT_MINUTES = 10;
const MILLISECONDS_PER_MINUTE = 60_000;
const CONFIRMATION_TIMEOUT_MS = CONFIRMATION_TIMEOUT_MINUTES * MILLISECONDS_PER_MINUTE;
const MAX_ACTION_ROUNDS = 3;
const MAX_CONFIRM_ATTEMPTS = 3;
const CLIENT_SECRET_MARKER = '_secret_';
const CLIENT_SECRET_PREFIX_LENGTH = 3;
const MAX_CLIENT_SECRET_LENGTH = 4096;
// Shared with Paypercut's payment-resource client-secret issuer. Checkout IDs are
// embedded only so the SDK can address the authoritative resource; merchants
// must continue to treat the complete client secret as opaque.
const MAX_RESOURCE_ID_LENGTH = 200;
const MAX_CAPABILITY_LENGTH = 4000;
const MAX_INSTRUMENT_ID_LENGTH = 256;
const PAYMENT_RESOURCE_ID_PATTERN = /^[A-Za-z0-9._:-]+$/;
const HTTP_SERVER_ERROR_START = 500;
const HTTP_PAYMENT_REQUIRED = 402;
const HTTP_REQUEST_TIMEOUT = 408;
const HTTP_TOO_MANY_REQUESTS = 429;
const REQUEST_TIMEOUT_MS = 30_000;
const CONFIRMATION_RECOVERABLE_STATUSES = new Set([HTTP_PAYMENT_REQUIRED]);
const HEX_RADIX = 16;
const HEX_MIN_WIDTH = 2;
const MIN_POLL_INTERVAL_MS = 250;
const STATUS_RETRY_DELAY_MS = 500;
const CONFIRM_RETRY_BASE_DELAY_MS = 250;
const EXPONENTIAL_BACKOFF_FACTOR = 2;

type CheckoutStatus = {
  checkoutSessionId: string;
  amount: number;
  currency: string;
  mode: 'payment' | 'setup' | 'subscription';
  state: 'active' | 'completed' | 'expired' | 'canceled';
  paymentObjectStatus:
    | 'canceled'
    | 'expired'
    | 'processing'
    | 'requires_action'
    | 'requires_capture'
    | 'requires_confirmation'
    | 'requires_payment_method'
    | 'succeeded'
    | null;
  paymentStatus: 'failed' | 'no_payment_required' | 'paid' | 'processing' | 'unpaid';
  terminal: boolean;
  retryable: boolean;
  pollingRecommended: boolean;
  recommendedAfterMs: number;
  nextAction: ElementsPaymentConfirmationUseSDKAction | null;
};

type CheckoutStatusCurrency = {
  iso: string;
  scale: number;
};

type ActiveAction = {
  id: string;
  port: MessagePort;
  resolve: (threeDSSessionId: string) => void;
  reject: (error: ElementsSubmitError) => void;
  timeoutId: number;
};

const CHECKOUT_STATES = new Set(['active', 'completed', 'expired', 'canceled']);
const PAYMENT_STATUSES = new Set(['failed', 'no_payment_required', 'paid', 'processing', 'unpaid']);
const PAYMENT_OBJECT_STATUSES = new Set([
  'canceled',
  'expired',
  'processing',
  'requires_action',
  'requires_capture',
  'requires_confirmation',
  'requires_payment_method',
  'succeeded',
]);

const parseCheckoutStatusCurrency = (value: unknown): CheckoutStatusCurrency => {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['iso', 'scale']) ||
    typeof value.iso !== 'string' ||
    !/^[A-Za-z]{3}$/.test(value.iso) ||
    !Number.isSafeInteger(value.scale) ||
    Number(value.scale) < 0
  ) {
    throw createSubmitError(
      'elements_checkout_status_invalid',
      'The payment status response was invalid.',
      false,
    );
  }

  return { iso: value.iso.toUpperCase(), scale: Number(value.scale) };
};

const parseCheckoutClientSecret = (clientSecret: unknown) => {
  if (
    !isBoundedProtocolString(clientSecret, MAX_CLIENT_SECRET_LENGTH) ||
    !clientSecret.startsWith('cs_')
  ) {
    throw createSubmitError(
      'elements_client_secret_invalid',
      'A valid Checkout Session client secret is required.',
      false,
    );
  }
  const markerIndex = clientSecret.indexOf(CLIENT_SECRET_MARKER, CLIENT_SECRET_PREFIX_LENGTH);
  const resourceId =
    markerIndex < 0 ? '' : clientSecret.slice(CLIENT_SECRET_PREFIX_LENGTH, markerIndex);
  const capability =
    markerIndex < 0 ? '' : clientSecret.slice(markerIndex + CLIENT_SECRET_MARKER.length);
  if (
    !isBoundedProtocolString(resourceId, MAX_RESOURCE_ID_LENGTH) ||
    !PAYMENT_RESOURCE_ID_PATTERN.test(resourceId) ||
    !isBoundedProtocolString(capability, MAX_CAPABILITY_LENGTH)
  ) {
    throw createSubmitError(
      'elements_client_secret_invalid',
      'A valid Checkout Session client secret is required.',
      false,
    );
  }
  return { clientSecret, checkoutSessionId: resourceId };
};

const parseUseSDKAction = (value: unknown): ElementsPaymentConfirmationUseSDKAction | null => {
  if (value === null || value === undefined) return null;
  if (
    !isRecord(value) ||
    value.type !== 'use_sdk' ||
    !isRecord(value.use_sdk) ||
    !hasExactKeys(value.use_sdk, ['sdk', 'metadata']) ||
    value.use_sdk.sdk !== 'three_d_secure' ||
    !isRecord(value.use_sdk.metadata)
  ) {
    throw createSubmitError(
      'elements_next_action_unsupported',
      'The payment requires an action that this SDK version cannot handle.',
      false,
    );
  }

  const metadata = value.use_sdk.metadata;
  const token = metadata.vault_token_ref;
  const tokenIntent = metadata.vault_token_intent_ref;
  const hasToken = isBoundedProtocolString(token, MAX_INSTRUMENT_ID_LENGTH);
  const hasTokenIntent = isBoundedProtocolString(tokenIntent, MAX_INSTRUMENT_ID_LENGTH);
  const expectedMetadataKeys = [
    'payment_intent',
    'payment_method',
    'attempt',
    'vault',
    hasToken ? 'vault_token_ref' : 'vault_token_intent_ref',
    'action_phase',
  ];
  if (
    hasToken === hasTokenIntent ||
    !hasExactKeys(metadata, expectedMetadataKeys) ||
    !isBoundedProtocolString(metadata.payment_intent, MAX_RESOURCE_ID_LENGTH) ||
    !isBoundedProtocolString(metadata.payment_method, MAX_RESOURCE_ID_LENGTH) ||
    !isBoundedProtocolString(metadata.attempt, MAX_RESOURCE_ID_LENGTH) ||
    metadata.vault !== 'bt' ||
    metadata.action_phase !== 'provider_recovery'
  ) {
    throw createSubmitError(
      'elements_next_action_invalid',
      'The payment authentication action was invalid.',
      false,
    );
  }

  return {
    paymentIntentId: metadata.payment_intent,
    paymentMethodId: metadata.payment_method,
    attemptId: metadata.attempt,
    vault: 'bt',
    instrument: hasToken
      ? { type: 'token', id: token }
      : { type: 'token_intent', id: tokenIntent as string },
    actionPhase: 'provider_recovery',
  };
};

const parseCheckoutStatus = (value: unknown, expectedCheckoutSessionId: string): CheckoutStatus => {
  if (
    !isRecord(value) ||
    value.checkout_session !== expectedCheckoutSessionId ||
    !Number.isSafeInteger(value.amount_total) ||
    Number(value.amount_total) < 0 ||
    typeof value.mode !== 'string' ||
    !['payment', 'setup', 'subscription'].includes(value.mode) ||
    typeof value.state !== 'string' ||
    !CHECKOUT_STATES.has(value.state) ||
    typeof value.payment_status !== 'string' ||
    !PAYMENT_STATUSES.has(value.payment_status) ||
    (value.payment_object_status !== null &&
      (typeof value.payment_object_status !== 'string' ||
        !PAYMENT_OBJECT_STATUSES.has(value.payment_object_status))) ||
    typeof value.terminal !== 'boolean' ||
    typeof value.retryable !== 'boolean' ||
    !isRecord(value.polling) ||
    typeof value.polling.recommended !== 'boolean' ||
    !Number.isSafeInteger(value.polling.recommended_after_ms) ||
    Number(value.polling.recommended_after_ms) < 0 ||
    Number(value.polling.recommended_after_ms) > CONFIRMATION_TIMEOUT_MS
  ) {
    throw createSubmitError(
      'elements_checkout_status_invalid',
      'The payment status response was invalid.',
      false,
    );
  }
  const currency = parseCheckoutStatusCurrency(value.currency);
  const nextAction = parseUseSDKAction(value.next_action);
  if (value.payment_object_status === 'requires_action' && nextAction === null) {
    throw createSubmitError(
      'elements_next_action_invalid',
      'The payment requires an action but no supported action was returned.',
      false,
    );
  }
  return {
    checkoutSessionId: expectedCheckoutSessionId,
    amount: Number(value.amount_total),
    currency: currency.iso,
    mode: value.mode as CheckoutStatus['mode'],
    state: value.state as CheckoutStatus['state'],
    paymentObjectStatus: value.payment_object_status as CheckoutStatus['paymentObjectStatus'],
    paymentStatus: value.payment_status as CheckoutStatus['paymentStatus'],
    terminal: value.terminal,
    retryable: value.retryable,
    pollingRecommended: value.polling.recommended,
    recommendedAfterMs: Number(value.polling.recommended_after_ms),
    nextAction,
  };
};

const resultFromStatus = (status: CheckoutStatus): PaypercutConfirmPaymentResult => ({
  checkoutSession: {
    id: status.checkoutSessionId,
    status: status.state,
    paymentStatus: status.paymentStatus,
    paymentObjectStatus: status.paymentObjectStatus,
  },
});

const wait = (delayMs: number) =>
  new Promise<void>((resolve) => globalThis.setTimeout(resolve, Math.max(0, delayMs)));

const isSubmitError = (error: unknown): error is ElementsSubmitError =>
  error instanceof Error &&
  'code' in error &&
  typeof error.code === 'string' &&
  'recoverable' in error &&
  typeof error.recoverable === 'boolean';

const abortReason = (signal: AbortSignal) =>
  signal.reason instanceof Error ? signal.reason : new DOMException('Aborted', 'AbortError');

const requestJSON = async (
  url: URL,
  init: RequestInit,
  errorCode: string,
  options: { deadlineAt: number; recoverableStatuses?: ReadonlySet<number> },
): Promise<unknown> => {
  const remainingMs = options.deadlineAt - Date.now();
  if (remainingMs <= 0) {
    throw createSubmitError(errorCode, 'The payment request timed out.', true);
  }
  const controller = new AbortController();
  const parentSignal = init.signal;
  const onAbort = () => controller.abort(parentSignal ? abortReason(parentSignal) : undefined);
  parentSignal?.addEventListener('abort', onAbort, { once: true });
  const timeoutId = globalThis.setTimeout(
    () => controller.abort(new DOMException('The payment request timed out.', 'TimeoutError')),
    Math.max(1, Math.min(REQUEST_TIMEOUT_MS, remainingMs)),
  );
  try {
    const response = await fetch(url, {
      ...init,
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!response.ok) {
      throw createSubmitError(
        errorCode,
        `The payment service rejected the request (HTTP ${response.status}).`,
        response.status >= HTTP_SERVER_ERROR_START ||
          response.status === HTTP_REQUEST_TIMEOUT ||
          response.status === HTTP_TOO_MANY_REQUESTS ||
          options.recoverableStatuses?.has(response.status) === true,
      );
    }
    try {
      return await response.json();
    } catch {
      throw createSubmitError(errorCode, 'The payment service returned an invalid response.', true);
    }
  } catch (error) {
    if (isSubmitError(error)) throw error;
    throw createSubmitError(errorCode, 'The payment service could not be reached.', true);
  } finally {
    globalThis.clearTimeout(timeoutId);
    parentSignal?.removeEventListener('abort', onAbort);
  }
};

const sameUseSDKAction = (
  left: ElementsPaymentConfirmationUseSDKAction,
  right: ElementsPaymentConfirmationUseSDKAction,
) =>
  left.paymentIntentId === right.paymentIntentId &&
  left.paymentMethodId === right.paymentMethodId &&
  left.attemptId === right.attemptId &&
  left.vault === right.vault &&
  left.instrument.type === right.instrument.type &&
  left.instrument.id === right.instrument.id &&
  left.actionPhase === right.actionPhase;

const digestIdempotencyKey = async (checkoutSessionId: string, threeDSSessionId: string) => {
  const bytes = new TextEncoder().encode(
    `paypercut-elements-confirm-v1:${checkoutSessionId}:${threeDSSessionId}`,
  );
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  const hexadecimal = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(HEX_RADIX).padStart(HEX_MIN_WIDTH, '0'),
  ).join('');
  return `paypercut-elements-confirm-v1-${hexadecimal}`;
};

export class PaymentConfirmationCoordinator {
  private readonly apiUrl: string;
  private readonly hostedOrigin: string;
  private readonly actionShell: ElementsActionShell;
  private readonly inFlight = new Map<string, Promise<PaypercutConfirmPaymentResult>>();
  private activeAction: ActiveAction | null = null;
  private readonly account?: string;

  constructor(
    private readonly publishableKey: string,
    options: { account?: string; apiUrl?: string; hostedCheckoutUrl?: string },
    private readonly billingDetailsFor: (
      paymentMethodId: string,
    ) => Pick<PaypercutBillingDetails, 'name' | 'email'> | undefined,
  ) {
    this.account = options.account;
    this.apiUrl = getApiOrigin(options.apiUrl);
    this.hostedOrigin = new URL(getCheckoutOrigin(options.hostedCheckoutUrl)).origin;
    this.actionShell = new ElementsActionShell(
      this.hostedOrigin,
      (actionId) =>
        this.rejectActiveAction(
          actionId,
          'elements_action_canceled',
          'Payment authentication was canceled.',
          true,
        ),
      (actionId, code, message, recoverable) =>
        this.rejectActiveAction(actionId, code, message, recoverable),
    );
  }

  confirm(clientSecret: string): Promise<PaypercutConfirmPaymentResult> {
    let parsed: ReturnType<typeof parseCheckoutClientSecret>;
    try {
      parsed = parseCheckoutClientSecret(clientSecret);
    } catch (error) {
      return Promise.reject(error);
    }
    const existing = this.inFlight.get(parsed.clientSecret);
    if (existing) return existing;
    const operation = this.run(parsed).finally(() => {
      if (this.inFlight.get(parsed.clientSecret) === operation) {
        this.inFlight.delete(parsed.clientSecret);
      }
    });
    this.inFlight.set(parsed.clientSecret, operation);
    return operation;
  }

  private async run(parsed: {
    clientSecret: string;
    checkoutSessionId: string;
  }): Promise<PaypercutConfirmPaymentResult> {
    const deadlineAt = Date.now() + CONFIRMATION_TIMEOUT_MS;
    let actionRounds = 0;
    let knownStatus: CheckoutStatus | null = null;
    while (Date.now() < deadlineAt) {
      let status: CheckoutStatus;
      try {
        status = knownStatus ?? (await this.getStatus(parsed, deadlineAt));
      } catch (error) {
        if (!isSubmitError(error) || !error.recoverable) throw error;
        const remainingMs = deadlineAt - Date.now();
        if (remainingMs <= 0) break;
        await wait(Math.min(STATUS_RETRY_DELAY_MS, remainingMs));
        continue;
      }
      knownStatus = null;
      if (status.terminal || status.paymentObjectStatus === 'processing') {
        return resultFromStatus(status);
      }
      if (status.nextAction) {
        if (status.mode !== 'payment') {
          throw createSubmitError(
            'elements_next_action_unsupported',
            'This confirmation action is only supported for payment-mode Checkout Sessions.',
            false,
          );
        }
        if (actionRounds >= MAX_ACTION_ROUNDS) {
          throw createSubmitError(
            'elements_next_action_loop',
            'The payment returned too many consecutive authentication actions.',
            false,
          );
        }
        actionRounds += 1;
        const completedAction = status.nextAction;
        const threeDSSessionId = await this.runAction(parsed, status, deadlineAt);
        knownStatus = await this.confirmCheckout(
          parsed,
          threeDSSessionId,
          completedAction,
          deadlineAt,
        );
        continue;
      }
      if (status.pollingRecommended) {
        const remainingMs = deadlineAt - Date.now();
        if (remainingMs <= 0) break;
        await wait(
          Math.min(remainingMs, Math.max(MIN_POLL_INTERVAL_MS, status.recommendedAfterMs)),
        );
        continue;
      }
      return resultFromStatus(status);
    }
    throw createSubmitError(
      'elements_action_timeout',
      'Payment confirmation timed out while waiting for an authoritative status.',
      true,
    );
  }

  private async getStatus(
    parsed: {
      clientSecret: string;
      checkoutSessionId: string;
    },
    deadlineAt: number,
  ): Promise<CheckoutStatus> {
    const url = new URL(
      `/v1/checkouts/${encodeURIComponent(parsed.checkoutSessionId)}/status`,
      this.apiUrl,
    );
    url.searchParams.set('key', this.publishableKey);
    url.searchParams.set('client_secret', parsed.clientSecret);
    const value = await requestJSON(
      url,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(this.account !== undefined ? { 'Paypercut-Account': this.account } : {}),
        },
      },
      'elements_checkout_status_failed',
      { deadlineAt },
    );
    return parseCheckoutStatus(value, parsed.checkoutSessionId);
  }

  private async confirmCheckout(
    parsed: { clientSecret: string; checkoutSessionId: string },
    threeDSSessionId: string,
    completedAction: ElementsPaymentConfirmationUseSDKAction,
    deadlineAt: number,
  ): Promise<CheckoutStatus | null> {
    const url = new URL(
      `/v1/checkouts/${encodeURIComponent(parsed.checkoutSessionId)}/confirm`,
      this.apiUrl,
    );
    const idempotencyKey = await digestIdempotencyKey(parsed.checkoutSessionId, threeDSSessionId);
    const request: RequestInit = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
        ...(this.account !== undefined ? { 'Paypercut-Account': this.account } : {}),
      },
      body: JSON.stringify({
        key: this.publishableKey,
        client_secret: parsed.clientSecret,
        payment_method_options: {
          type: 'card',
          card: { three_d_secure: { session_id: threeDSSessionId } },
        },
      }),
    };

    let lastError: ElementsSubmitError | null = null;
    for (let attempt = 0; attempt < MAX_CONFIRM_ATTEMPTS; attempt += 1) {
      try {
        await requestJSON(url, request, 'elements_checkout_confirmation_failed', {
          deadlineAt,
          recoverableStatuses: CONFIRMATION_RECOVERABLE_STATUSES,
        });
        return null;
      } catch (error) {
        if (!isSubmitError(error) || !error.recoverable) throw error;
        lastError = error;

        // A timeout or dropped response does not prove that confirmation failed.
        // Reconcile first; only replay the exact same request under the exact same
        // idempotency key when the authoritative state still requires the action.
        try {
          const status = await this.getStatus(parsed, deadlineAt);
          if (
            status.terminal ||
            status.paymentObjectStatus === 'processing' ||
            status.nextAction === null ||
            !sameUseSDKAction(status.nextAction, completedAction)
          ) {
            return status;
          }
        } catch (statusError) {
          if (isSubmitError(statusError) && !statusError.recoverable) throw statusError;
        }

        if (attempt + 1 < MAX_CONFIRM_ATTEMPTS) {
          const remainingMs = deadlineAt - Date.now();
          if (remainingMs <= 0) break;
          await wait(
            Math.min(
              remainingMs,
              CONFIRM_RETRY_BASE_DELAY_MS * EXPONENTIAL_BACKOFF_FACTOR ** attempt,
            ),
          );
        }
      }
    }
    throw (
      lastError ??
      createSubmitError(
        'elements_checkout_confirmation_failed',
        'The payment confirmation result is unknown.',
        true,
      )
    );
  }

  private runAction(
    parsed: { clientSecret: string; checkoutSessionId: string },
    status: CheckoutStatus,
    deadlineAt: number,
  ): Promise<string> {
    if (!status.nextAction) {
      return Promise.reject(
        createSubmitError('elements_next_action_invalid', 'No payment action is available.', false),
      );
    }
    if (this.activeAction) {
      return Promise.reject(
        createSubmitError(
          'elements_action_in_progress',
          'Another payment authentication is already in progress.',
          true,
        ),
      );
    }
    const actionId = generateId();
    const channel = new MessageChannel();
    return new Promise<string>((resolve, reject) => {
      const timeoutId = globalThis.setTimeout(
        () => {
          this.rejectActiveAction(
            actionId,
            'elements_action_timeout',
            'Payment authentication timed out.',
            true,
          );
        },
        Math.max(1, deadlineAt - Date.now()),
      );
      this.activeAction = { id: actionId, port: channel.port1, resolve, reject, timeoutId };
      channel.port1.onmessage = (event: MessageEvent) => {
        const active = this.activeAction;
        if (
          !active ||
          active.id !== actionId ||
          !isElementsPaymentConfirmationMessage(event.data) ||
          event.data.actionId !== actionId
        ) {
          return;
        }
        if (event.data.type === 'ready') {
          const billingDetails = this.billingDetailsFor(status.nextAction!.paymentMethodId);
          const message: ElementsPaymentConfirmationStart = {
            type: 'start',
            actionId,
            payload: {
              protocolVersion: ELEMENTS_PAYMENT_CONFIRMATION_PROTOCOL_VERSION,
              publishableKey: this.publishableKey,
              ...(this.account !== undefined ? { account: this.account } : {}),
              apiUrl: this.apiUrl,
              sdkVersion: config.version,
              deadlineAt,
              checkoutSession: {
                id: parsed.checkoutSessionId,
                amount: status.amount,
                currency: status.currency,
                mode: status.mode,
              },
              action: status.nextAction!,
              ...(billingDetails ? { billingDetails } : {}),
            },
          };
          active.port.postMessage(message);
          return;
        }
        if (event.data.type === 'complete') {
          this.resolveActiveAction(actionId, event.data.payload.threeDSSessionId);
        }
      };
      channel.port1.start();
      try {
        this.actionShell.show(
          { id: actionId, type: 'three_d_secure', source: 'payment_confirmation' },
          channel.port2,
        );
      } catch {
        channel.port2.close();
        this.rejectActiveAction(
          actionId,
          'elements_action_channel_unavailable',
          'The secure authentication window could not be opened.',
          true,
        );
      }
    });
  }

  private resolveActiveAction(actionId: string, threeDSSessionId: string): void {
    const active = this.activeAction;
    if (!active || active.id !== actionId) return;
    this.activeAction = null;
    globalThis.clearTimeout(active.timeoutId);
    active.port.close();
    this.actionShell.close();
    active.resolve(threeDSSessionId);
  }

  private rejectActiveAction(
    actionId: string,
    code: string,
    message: string,
    recoverable: boolean,
  ): void {
    const active = this.activeAction;
    if (!active || active.id !== actionId) return;
    this.activeAction = null;
    globalThis.clearTimeout(active.timeoutId);
    active.port.close();
    this.actionShell.close();
    active.reject(createSubmitError(code, message, recoverable));
  }
}
